import { NavLink, useLocation } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { Logo } from '@/components/Logo'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { api } from '@/lib/api'
import { CmdKBadge } from './CommandPalette'
import { Modal } from './Modal'
import { useTranslation } from '@/i18n/useTranslation'

// ─── Types ───────────────────────────────────────────────────────────────────

interface NavItem {
  label: string
  to: string
  icon: string
}

interface NavSection {
  key: string
  title: string
  items: NavItem[]
}

// ─── Navigation Architecture ─────────────────────────────────────────────────
// Design principle: Progressive Disclosure + Hick's Law
// Show 5 primary actions always visible, group secondary under collapsible sections.
// Primary items = what streamers use every session (go live, chat, alerts, clips, analytics).

const PRIMARY_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/', icon: '⊞' },
  { label: 'Go Live', to: '/live', icon: '▶' },
  { label: 'Command Center', to: '/command', icon: '⊹' },
  { label: 'Chat', to: '/chat', icon: '💬' },
  { label: 'Alerts', to: '/alerts', icon: '🔔' },
  { label: 'Analytics', to: '/analytics', icon: '📊' },
]

const NAV_SECTIONS: NavSection[] = [
  {
    key: 'stream',
    title: 'Streaming',
    items: [
      { label: 'Studio', to: '/studio', icon: '◲' },
      { label: 'Stream Setup', to: '/setup', icon: '🚀' },
      { label: 'Stream Keys', to: '/stream-keys', icon: '⊗' },
      { label: 'Stream Info', to: '/stream-info', icon: '📝' },
      { label: 'Stream Health', to: '/health', icon: '♡' },
      { label: 'Multistream', to: '/multistream', icon: '⊶' },
      { label: 'Schedule', to: '/schedule', icon: '📅' },
      { label: 'Music', to: '/music', icon: '♫' },
      { label: 'Soundboard', to: '/soundboard', icon: '⊞' },
      { label: 'Hotkeys', to: '/hotkeys', icon: '⌘' },
    ],
  },
  {
    key: 'content',
    title: 'Content',
    items: [
      { label: 'Clips', to: '/clips', icon: '◈' },
      { label: 'VODs', to: '/vods', icon: '▣' },
      { label: 'Highlights', to: '/highlights', icon: '✂' },
      { label: 'Chat Overlay', to: '/chat-overlay', icon: '◫' },
      { label: 'Overlays', to: '/overlays', icon: '◐' },
      { label: 'Widgets', to: '/widgets', icon: '◇' },
      { label: 'Marketplace', to: '/marketplace', icon: '◆' },
      { label: 'Themes', to: '/themes', icon: '◎' },
    ],
  },
  {
    key: 'community',
    title: 'Community',
    items: [
      { label: 'Chat Bot', to: '/chatbot', icon: '⊳' },
      { label: 'Commands', to: '/commands', icon: '!' },
      { label: 'Timers', to: '/timers', icon: '⊙' },
      { label: 'Polls', to: '/polls', icon: '◉' },
      { label: 'Giveaways', to: '/giveaways', icon: '⊛' },
      { label: 'Contests', to: '/contests', icon: '⊕' },
      { label: 'Channel Points', to: '/points', icon: '★' },
      { label: 'Redemptions', to: '/redemptions', icon: '🎁' },
      { label: 'Emotes', to: '/emotes', icon: '☺' },
      { label: 'Quotes', to: '/quotes', icon: '"' },
      { label: 'Media Requests', to: '/media-requests', icon: '◈' },
      { label: 'Viewer Queue', to: '/viewer-queue', icon: '⊶' },
      { label: 'Viewer Roles', to: '/viewer-roles', icon: '⊚' },
      { label: 'Raids', to: '/raids', icon: '⚡' },
      { label: 'Watch Party', to: '/watchparty', icon: '▣' },
      { label: 'Teams', to: '/teams', icon: '⊕' },
      { label: 'Collab', to: '/collab', icon: '⊕' },
      { label: 'Discord', to: '/discord', icon: '⊷' },
    ],
  },
  {
    key: 'analytics',
    title: 'Analytics',
    items: [
      { label: 'Stream Report', to: '/stream-report', icon: '📈' },
      { label: 'Chat Stats', to: '/chat-stats', icon: '◉' },
      { label: 'Recent Events', to: '/events', icon: '◴' },
      { label: 'Sessions', to: '/sessions', icon: '📡' },
      { label: 'Activity', to: '/activity', icon: '◴' },
    ],
  },
  {
    key: 'growth',
    title: 'Growth',
    items: [
      { label: 'Subscribers', to: '/subscribers', icon: '♜' },
      { label: 'Follows', to: '/follows', icon: '♡' },
      { label: 'Loyalty', to: '/loyalty', icon: '★' },
      { label: 'Goals', to: '/goals', icon: '◫' },
      { label: 'Counters', to: '/counters', icon: '⊞' },
      { label: 'Leaderboard', to: '/leaderboard', icon: '⊛' },
      { label: 'Coaching', to: '/coaching', icon: '⬡' },
      { label: 'Intelligence', to: '/intelligence', icon: '⬡' },
    ],
  },
  {
    key: 'monetization',
    title: 'Monetization',
    items: [
      { label: 'Tips & Revenue', to: '/tips', icon: '⊛' },
      { label: 'Merch Store', to: '/merch', icon: '◆' },
      { label: 'Sponsors', to: '/sponsors', icon: '$' },
    ],
  },
  {
    key: 'tools',
    title: 'Tools',
    items: [
      { label: 'Alert Editor', to: '/alerts/editor', icon: '⚡' },
      { label: 'Moderation', to: '/moderation', icon: '⊘' },
      { label: 'AutoMod', to: '/automod', icon: '🛡' },
      { label: 'Webhooks', to: '/webhooks', icon: '⇄' },
      { label: 'Import / Export', to: '/import-export', icon: '⇄' },
    ],
  },
  {
    key: 'settings',
    title: 'Settings',
    items: [
      { label: 'Connections', to: '/connections', icon: '⊷' },
      { label: 'Billing', to: '/billing', icon: '⊙' },
      { label: 'API Docs', to: '/api-docs', icon: '⊡' },
    ],
  },
]

const BOTTOM_ITEMS: NavItem[] = [
  { label: 'Profile', to: '/profile', icon: '⊚' },
  { label: 'Badges', to: '/badges', icon: '◈' },
  { label: 'Settings', to: '/settings', icon: '⚙' },
]

const ALL_ITEMS: NavItem[] = [
  ...PRIMARY_ITEMS,
  ...NAV_SECTIONS.flatMap((s) => s.items),
  ...BOTTOM_ITEMS,
]

// Map from route path → NavItem for quick lookup
const ITEM_BY_PATH = new Map<string, NavItem>(ALL_ITEMS.map((item) => [item.to, item]))

// Default favorites: the 5 essentials a streamer needs every session
const DEFAULT_FAVORITES: string[] = ['/', '/live', '/chat', '/alerts', '/analytics']

const MAX_FAVORITES = 8
const MAX_RECENT = 5

// ─── Helpers ─────────────────────────────────────────────────────────────────

interface UnreadCountResponse {
  count: number
}

function defaultCollapsedState(): Record<string, boolean> {
  return Object.fromEntries(NAV_SECTIONS.map((s) => [s.key, true]))
}

// ─── FavoritesModal ──────────────────────────────────────────────────────────

interface FavoritesModalProps {
  open: boolean
  onClose: () => void
  favorites: string[]
  onReorder: (paths: string[]) => void
  onRemove: (path: string) => void
}

function FavoritesModal({ open, onClose, favorites, onReorder, onRemove }: FavoritesModalProps) {
  const { t } = useTranslation()
  function moveUp(idx: number) {
    if (idx === 0) return
    const next = [...favorites]
    ;[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
    onReorder(next)
  }

  function moveDown(idx: number) {
    if (idx === favorites.length - 1) return
    const next = [...favorites]
    ;[next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]
    onReorder(next)
  }

  return (
    <Modal open={open} onClose={onClose} title={t('nav.favorites')} maxWidth="26rem">
      <p className="text-sm text-gray-400 mb-4">
        Reorder or remove pinned pages. Up to {MAX_FAVORITES} favorites.
      </p>

      {favorites.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">
          No favorites yet. Hover any nav item and click the star to pin it.
        </p>
      ) : (
        <ul className="space-y-1.5" role="list" aria-label="Favorite pages">
          {favorites.map((path, idx) => {
            const item = ITEM_BY_PATH.get(path)
            if (!item) return null
            return (
              <li
                key={path}
                className="flex items-center gap-2 rounded-lg px-3 py-2"
                style={{ backgroundColor: 'var(--bg-primary)' }}
              >
                <span className="w-5 text-center text-sm shrink-0" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="flex-1 text-sm text-gray-200 truncate">{item.label}</span>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                    className="w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs"
                    aria-label={`Move ${item.label} up`}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(idx)}
                    disabled={idx === favorites.length - 1}
                    className="w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs"
                    aria-label={`Move ${item.label} down`}
                  >
                    ▼
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemove(path)}
                    className="w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:text-red-400 transition-colors text-xs ml-1"
                    aria-label={`Remove ${item.label} from favorites`}
                  >
                    ✕
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
          style={{ backgroundColor: 'var(--accent)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--accent-hover)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--accent)'
          }}
        >
          {t('common.done')}
        </button>
      </div>
    </Modal>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

interface SidebarProps {
  mobileOpen: boolean
  onMobileClose: () => void
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const { user, logout } = useAuth()
  const { t } = useTranslation()
  const location = useLocation()
  const [unreadCount, setUnreadCount] = useState(0)
  const sidebarRef = useRef<HTMLElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [collapsed, setCollapsed] = useLocalStorage<Record<string, boolean>>(
    'kast-sidebar-collapsed-v2',
    defaultCollapsedState(),
  )
  const [favorites, setFavorites] = useLocalStorage<string[]>(
    'kast-sidebar-favorites-v1',
    DEFAULT_FAVORITES,
  )
  const [recentPaths, setRecentPaths] = useLocalStorage<string[]>(
    'kast-sidebar-recent-v1',
    [],
  )
  const [favoritesOpen, setFavoritesOpen] = useState(false)

  // Track navigation for recent pages
  useEffect(() => {
    const path = location.pathname
    setRecentPaths((prev) => {
      // Only track paths that exist in the nav
      if (!ITEM_BY_PATH.has(path)) return prev
      const next = [path, ...prev.filter((p) => p !== path)].slice(0, MAX_RECENT)
      return next
    })
  }, [location.pathname, setRecentPaths])

  const isSearching = searchQuery.trim().length > 0

  const filteredItems = useMemo(() => {
    if (!isSearching) return []
    const q = searchQuery.trim().toLowerCase()
    return ALL_ITEMS.filter((item) => item.label.toLowerCase().includes(q))
  }, [searchQuery, isSearching])

  const favoriteItems = useMemo(
    () => favorites.flatMap((path) => (ITEM_BY_PATH.get(path) ? [ITEM_BY_PATH.get(path)!] : [])),
    [favorites],
  )

  // Recent = last visited, excluding anything already in favorites
  const recentItems = useMemo(
    () =>
      recentPaths
        .filter((p) => !favorites.includes(p))
        .flatMap((path) => (ITEM_BY_PATH.get(path) ? [ITEM_BY_PATH.get(path)!] : []))
        .slice(0, MAX_RECENT),
    [recentPaths, favorites],
  )

  const toggleFavorite = useCallback(
    (path: string) => {
      setFavorites((prev) => {
        if (prev.includes(path)) {
          return prev.filter((p) => p !== path)
        }
        if (prev.length >= MAX_FAVORITES) return prev
        return [...prev, path]
      })
    },
    [setFavorites],
  )

  const isFavorited = useCallback((path: string) => favorites.includes(path), [favorites])

  useEffect(() => {
    let cancelled = false
    const fetchCount = () => {
      api
        .get<UnreadCountResponse>('/notifications/unread-count')
        .then((res) => {
          if (!cancelled) setUnreadCount(res.count ?? 0)
        })
        .catch(() => null)
    }
    fetchCount()
    const interval = setInterval(fetchCount, 30_000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (
        mobileOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        onMobileClose()
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [mobileOpen, onMobileClose])

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && mobileOpen) onMobileClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [mobileOpen, onMobileClose])

  function toggleSection(key: string) {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  // Expert nav-item treatment: a fixed-width icon slot keeps every label on a
  // single vertical rhythm column (no ragged glyph widths), an inset accent bar
  // marks the active route, and hover/active share one tinted-surface language.
  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'group/navitem relative flex items-center gap-3 rounded-lg pl-3 pr-2.5 py-2 text-sm font-medium',
      'transition-all duration-150 ease-out',
      'max-md:justify-start md:justify-center lg:justify-start',
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500',
      isActive ? 'font-semibold' : 'text-gray-400 hover:text-white',
    ].join(' ')

  const navLinkStyle = ({ isActive }: { isActive: boolean }) =>
    isActive
      ? {
          backgroundColor: 'var(--accent-muted)',
          color: 'var(--accent-light)',
        }
      : {}

  function renderNavItem(item: NavItem, showPin = false) {
    const pinned = isFavorited(item.to)
    return (
      <div key={item.to} className="group/pinrow relative">
        <NavLink
          to={item.to}
          end={item.to === '/'}
          className={navLinkClass}
          style={navLinkStyle}
          title={item.label}
          onMouseEnter={(e) => {
            if (!e.currentTarget.classList.contains('active'))
              e.currentTarget.style.backgroundColor = 'var(--bg-card)'
          }}
          onMouseLeave={(e) => {
            if (!e.currentTarget.classList.contains('active'))
              e.currentTarget.style.backgroundColor = ''
          }}
        >
          {/* Active accent bar — inset on the left edge, hidden on the icon rail */}
          <span
            aria-hidden="true"
            className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-full transition-all duration-150 hidden lg:block max-md:block group-[.active]/navitem:h-5 h-0 opacity-0 group-[.active]/navitem:opacity-100"
            style={{ backgroundColor: 'var(--accent-light)' }}
          />
          <span
            className="flex items-center justify-center w-5 h-5 text-base leading-none shrink-0"
            aria-hidden="true"
          >
            {item.icon}
          </span>
          <span className="hidden lg:inline max-md:inline truncate flex-1">{item.label}</span>
        </NavLink>

        {/* Pin button — visible on hover, always visible if pinned, lg/mobile only */}
        {showPin && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              toggleFavorite(item.to)
            }}
            aria-label={pinned ? `Unpin ${item.label}` : `Pin ${item.label} to favorites`}
            aria-pressed={pinned}
            className={[
              'absolute right-2 top-1/2 -translate-y-1/2',
              'hidden lg:flex max-md:flex items-center justify-center',
              'w-5 h-5 rounded text-[11px] transition-all duration-150',
              pinned
                ? 'opacity-100 text-yellow-400'
                : 'opacity-0 group-hover/pinrow:opacity-100 text-gray-600 hover:text-gray-300',
              favorites.length >= MAX_FAVORITES && !pinned
                ? 'cursor-not-allowed opacity-30 group-hover/pinrow:opacity-30'
                : '',
            ].join(' ')}
            title={
              favorites.length >= MAX_FAVORITES && !pinned
                ? `Max ${MAX_FAVORITES} favorites`
                : pinned
                  ? 'Unpin'
                  : 'Pin to favorites'
            }
          >
            {pinned ? '★' : '☆'}
          </button>
        )}
      </div>
    )
  }

  return (
    <>
      <FavoritesModal
        open={favoritesOpen}
        onClose={() => setFavoritesOpen(false)}
        favorites={favorites}
        onReorder={setFavorites}
        onRemove={(path) => setFavorites((prev) => prev.filter((p) => p !== path))}
      />

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-hidden="true"
        />
      )}

      <aside
        ref={sidebarRef}
        id="sidebar"
        aria-label="Main navigation"
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRight: '1px solid var(--border-primary)',
        }}
        className={[
          'flex flex-col min-h-screen shrink-0 transition-transform duration-200',
          'md:relative md:translate-x-0 md:w-16 lg:w-64',
          'max-md:fixed max-md:top-0 max-md:left-0 max-md:h-full max-md:z-50 max-md:w-64',
          mobileOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full',
        ].join(' ')}
      >
        {/* Brand header */}
        <div className="flex items-center gap-2.5 h-16 px-3 lg:px-5 max-md:justify-start md:justify-center lg:justify-start border-b border-white/5 shrink-0">
          <Logo size={32} className="shrink-0" />
          <span className="text-white font-semibold text-lg tracking-tight hidden lg:block max-md:block">
            Kast
          </span>
        </div>

        {/* Search filter */}
        <div className="px-3 pt-4 pb-2 hidden lg:block max-md:block">
          <div className="relative group/search">
            <span
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none transition-colors group-focus-within/search:text-[color:var(--accent-light)]"
              aria-hidden="true"
            >
              ⌕
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('nav.searchMenu')}
              aria-label={t('nav.searchMenu')}
              className="w-full rounded-lg py-2 pl-8 pr-8 text-sm text-gray-200 placeholder-gray-600 transition-colors focus:outline-none focus:border-[color:var(--accent)] focus:ring-1 focus:ring-purple-500/40"
              style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-primary)' }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-200 text-xs transition-colors"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto" aria-label="Primary">
          {isSearching ? (
            <div className="space-y-1">
              <p className="px-3 pt-1 pb-2 text-[10px] font-semibold tracking-widest uppercase text-gray-600 hidden lg:block max-md:block">
                {filteredItems.length === 1
                  ? t('nav.searchResults', { count: 1 })
                  : t('nav.searchResults_plural', { count: filteredItems.length })}
              </p>
              {filteredItems.length === 0 ? (
                <p className="px-3 py-6 text-xs text-gray-500 text-center">
                  {t('nav.noResults', { query: searchQuery })}
                </p>
              ) : (
                filteredItems.map((item) => renderNavItem(item, true))
              )}
            </div>
          ) : (
            <>
              {/* ── Favorites ─────────────────────────────────────── */}
              {favoriteItems.length > 0 && (
                <div className="mb-1">
                  <div className="hidden lg:flex max-md:flex items-center justify-between px-3 pt-3 pb-1.5">
                    <span
                      className="text-[10px] font-semibold tracking-[0.12em] uppercase select-none"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {t('nav.favorites')}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFavoritesOpen(true)}
                      className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors"
                      aria-label={t('nav.manage')}
                      title={t('nav.manage')}
                    >
                      {t('nav.manage')}
                    </button>
                  </div>
                  <div className="space-y-1">
                    {favoriteItems.map((item) => renderNavItem(item, true))}
                  </div>
                </div>
              )}

              {/* ── Recent pages ──────────────────────────────────── */}
              {recentItems.length > 0 && (
                <div className="mb-1">
                  <div className="hidden lg:flex max-md:flex items-center px-3 pt-3 pb-1.5">
                    <span
                      className="text-[10px] font-semibold tracking-[0.12em] uppercase select-none"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {t('nav.recent')}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {recentItems.map((item) => renderNavItem(item, true))}
                  </div>
                </div>
              )}

              {/* Visual separator between favorites/recent and full nav */}
              {(favoriteItems.length > 0 || recentItems.length > 0) && (
                <div
                  className="hidden lg:block max-md:block my-3 mx-1 border-t"
                  style={{ borderColor: 'var(--border-primary)' }}
                  aria-hidden="true"
                />
              )}

              {/* ── All pages (collapsed sections) ────────────────── */}
              {NAV_SECTIONS.map((section) => {
                const isCollapsed = collapsed[section.key] ?? true
                return (
                  <div key={section.key} className="mb-1">
                    <button
                      onClick={() => toggleSection(section.key)}
                      className="w-full flex items-center justify-between gap-2 px-3 pt-3 pb-1.5 rounded-lg group/section cursor-pointer hidden lg:flex max-md:flex"
                      aria-expanded={!isCollapsed}
                      aria-controls={`section-${section.key}`}
                    >
                      <span
                        className="text-[10px] font-semibold tracking-[0.12em] uppercase select-none transition-colors group-hover/section:text-gray-400"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        {section.title}
                      </span>
                      <span
                        className="text-[9px] transition-all duration-200 text-gray-600 group-hover/section:text-gray-400"
                        style={{
                          transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        }}
                        aria-hidden="true"
                      >
                        ▾
                      </span>
                    </button>

                    <div
                      id={`section-${section.key}`}
                      className="space-y-1 overflow-hidden transition-all duration-200 ease-out"
                      style={{
                        maxHeight: isCollapsed ? '0px' : `${section.items.length * 44}px`,
                        opacity: isCollapsed ? 0 : 1,
                      }}
                    >
                      {section.items.map((item) => renderNavItem(item, true))}
                    </div>
                  </div>
                )
              })}
            </>
          )}

          {/* Notifications */}
          <div className="mt-4 pt-4 border-t border-white/5">
            <NavLink
              to="/notifications"
              className={navLinkClass}
              style={navLinkStyle}
              title="Notifications"
            >
              <span
                aria-hidden="true"
                className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-full transition-all duration-150 hidden lg:block max-md:block group-[.active]/navitem:h-5 h-0 opacity-0 group-[.active]/navitem:opacity-100"
                style={{ backgroundColor: 'var(--accent-light)' }}
              />
              <span
                className="flex items-center justify-center w-5 h-5 text-base leading-none relative shrink-0"
                aria-hidden="true"
              >
                🔔
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 text-white font-bold rounded-full flex items-center justify-center ring-2"
                    style={{
                      backgroundColor: 'var(--accent)',
                      fontSize: '9px',
                      minWidth: '15px',
                      height: '15px',
                      lineHeight: '15px',
                      padding: '0 3px',
                      // ring blends the badge into the sidebar surface
                      ['--tw-ring-color' as string]: 'var(--bg-primary)',
                    }}
                    aria-label={`${unreadCount > 99 ? '99+' : unreadCount} unread notifications`}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </span>
              <span className="hidden lg:inline max-md:inline truncate">Notifications</span>
            </NavLink>
          </div>

          {/* Bottom pinned — account & config, kept distinct from primary nav */}
          <div className="mt-4 pt-4 border-t border-white/5 space-y-1">
            {BOTTOM_ITEMS.map((item) => renderNavItem(item, false))}
          </div>
        </nav>

        {/* Command palette hint */}
        <div className="px-3 pb-3 hidden lg:flex max-md:flex justify-center">
          <CmdKBadge />
        </div>

        {/* User footer */}
        {user && (
          <div className="px-3 py-3 border-t border-white/5">
            <div
              className="flex items-center gap-3 px-2.5 py-2 rounded-xl transition-colors max-md:justify-start md:justify-center lg:justify-start"
              style={{ backgroundColor: 'var(--bg-card)' }}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ring-1"
                style={{
                  background: 'linear-gradient(135deg, var(--accent-light), var(--accent-hover))',
                  ['--tw-ring-color' as string]: 'rgba(255,255,255,0.1)',
                }}
                aria-hidden="true"
              >
                {(user.displayName || user.username).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0 hidden lg:block max-md:block">
                <p className="text-white text-sm font-medium truncate leading-tight">
                  {user.displayName || user.username}
                </p>
                <p className="text-gray-500 text-xs truncate mt-0.5">{user.email}</p>
              </div>
              <button
                onClick={logout}
                className="shrink-0 flex items-center justify-center w-7 h-7 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors text-sm hidden lg:flex max-md:flex focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
                aria-label={t('auth.logout')}
                title={t('auth.logout')}
              >
                ↩
              </button>
            </div>
          </div>
        )}
      </aside>
    </>
  )
}
